import React, { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useForceUpdate } from '../../hooks/useForceUpdate';
import { memo } from '../../utils';
import { getNodeFromSelector } from '../../utils/dom';

export const PurePortal = forwardRef(({ node, selector, children }, forwardedRef) => {
	// const context = useContext(PortalContext);
	const childContext = useRef({ children: [] });
	const mountNode = useRef(null);
	const portalNode = useRef(null);
	const forceUpdate = useForceUpdate();
	const getParent = useMemo(() => memo(getNodeFromSelector), []);

	useLayoutEffect(() => {
		// This ref may be null when a hot-loader replaces components on the page
		if (!mountNode.current) return;

		let ownerDocument = mountNode.current.ownerDocument;
		const parent = getParent(selector);
		// It's possible that the content of the portal has, itself, been portaled.
		// In that case, it's important to append to the correct document element.
		let body = parent || ownerDocument.body;
		portalNode.current = node;
		body.appendChild(portalNode.current);
		forceUpdate();

		return () => {
			if (portalNode.current && body) {
				body.removeChild(portalNode.current);
			}
		};
	}, [node, forceUpdate, selector]);

	useImperativeHandle(
		forwardedRef,
		() => {
			const getContainer = memo((selector) => {
				const node = getNodeFromSelector(selector);
				if (!node) {
					return node;
				}
				return node;
			});

			return {
				contains(checkNode) {
					const container = getContainer(node);
					if (!container) {
						return false;
					}
					if (container.contains(checkNode)) {
						return true;
					}
					for (const child of childContext.current.children) {
						if (child.contains(checkNode)) {
							return true;
						}
					}
					return false;
				},
			};
		},
		[selector],
	);

	return portalNode.current ? createPortal(children, portalNode.current) : <span ref={mountNode} />;
});

PurePortal.displayName = 'PurePortal';
